import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resources } from '../../resources/resources';
import Icons from '../../../../common/widgets/Icons';
import Dialog from '../../../../common/widgets/Dialog';
import Button from '../../../../common/widgets/Button';
import { config } from '../../config';
import VideoThumbnail from './videoThumbnail';
import { tutorialsService } from '../../services/tutorials';
import { TutorialVideo, TutorialsModel } from '../../models/TutorialsModel';
import open_tutorials from '../../../../common/assets/images/tutorials.gif';
export interface TutorialsPanelProps {
  isVisible: boolean;
  currentRoute: string | undefined;
  categoryId: string | undefined;
  forMobile: boolean;
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

export default function TutorialsPanel(props: Readonly<TutorialsPanelProps>) {
  const { t: translate } = useTranslation();
  const { isVisible, currentRoute, categoryId, forMobile } = props;
  const [selectedVideo, setSelectedVideo] = useState<TutorialVideo | null>(
    null,
  );
  const [tutorials, setTutorials] = useState<TutorialsModel | null>(null);

  useEffect(() => {
    if (!currentRoute && !categoryId) return;

    let isCancelled = false;
    const fetchTutorials = async () => {
      if (isCancelled) {
        return;
      }

      const response = await tutorialsService.getTutorials(
        currentRoute,
        categoryId,
      );

      if (!response.hasErrors) {
        setTutorials(response.data);
      }
    };

    fetchTutorials();
    setIsExpanded(false);
    return () => {
      isCancelled = true;
    };
  }, [currentRoute, categoryId]);

  const closeDialog = () => {
    setSelectedVideo(null);
  };

  const [isExpanded, setIsExpanded] = useState<boolean>(isVisible);
  const thumbnailCache = useRef(new Map());

  const videoItems = useMemo(
    () =>
      _.map(tutorials?.tutorialVideos, (t: TutorialVideo) => (
        <div key={t.id} className="tutorials-video">
          <div className="tutorials-video-header">
            <strong>
              {t.videoTitle ?? translate(resources.tutorials.genericTitle)}
            </strong>
            <div className="tutorials-video-time">
              <Icons.TutorialsVideoClock />
              <span>{formatTime(t.duration ?? 0)}</span>
            </div>
          </div>
          <div className="tutorials-video-icon">
            <button onClick={() => setSelectedVideo(t)}>
              <VideoThumbnail
                thumbnailsrc={`${config.videosUrl}/${t.thumbnailUrl}`}
                videosrc={`${config.videosUrl}/${t.videoUrl}`}
                cache={thumbnailCache.current}
              ></VideoThumbnail>
            </button>
          </div>
        </div>
      )),
    [tutorials?.tutorialVideos, translate],
  );

  const videoPopup = () => {
    return (
      <div className="tutorials-wrapper-bottom">
        <div
          className={
            forMobile
              ? 'tutorials-wrapper-bottom-host-mobile'
              : 'tutorials-wrapper-bottom-host'
          }
        >
          {isExpanded && (
            <div className="tutorials-panel">
              <div className="tutorials-panel-header">
                <h2 className="tutorials-title">
                  {translate(resources.tutorials.title)}
                </h2>
                <div className="tutorials-panel-close">
                  <button onClick={() => setIsExpanded(!isExpanded)}>
                    <Icons.NotificationsClose />
                  </button>
                </div>
              </div>
              <div className="tutorials-video-list">{videoItems}</div>
              <div className="tutorials-panel-footer">
                <Icons.TutorialslHelp />
                <a href="/help" className="tutorials-help-link">
                  <h5 className="tutorials-help">
                    {translate(resources.tutorials.help)}
                  </h5>
                </a>
              </div>
            </div>
          )}

          <div className="tutorials-toggle-button">
            <div className="tutorials-toggle-button-icon">
              <button onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? (
                  <Icons.TutorialsClose />
                ) : (
                  <img src={open_tutorials} alt="open_tutorials" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const videoDialog = () => {
    return (
      <Dialog
        closeDialog={closeDialog}
        closeOnClickOutside
        className="dialog-xxxl tutorials-video-dialog"
      >
        <div className="tutorials-video-dialog-header">
          <div className="tutorials-video-dialog-close">
            <Button onClick={() => closeDialog()}>
              <Icons.NotificationsClose />
            </Button>
          </div>
        </div>
        <div className="dialog-content">
          <div className="dialog-video-wrapper">
            <video
              width="600"
              height="400"
              controls
              autoPlay
              className="tutorials-dialog-video-player"
            >
              <source
                key={selectedVideo?.id}
                src={`${config.videosUrl}/${selectedVideo?.videoUrl}`}
              />
              <track kind="captions" src="" label="No captions available" />
            </video>
          </div>
        </div>
        <div className="tutorials-video-dialog-footer">
          <div>
            <strong>
              {selectedVideo?.videoDescr ??
                translate(resources.tutorials.genericDescr)}
            </strong>
          </div>
          <div className="tutorials-video-time">
            <Icons.TutorialsVideoClock />
            <span>{formatTime(selectedVideo?.duration ?? 0)}</span>
          </div>
        </div>
      </Dialog>
    );
  };

  return (
    <>
      {tutorials && tutorials.tutorialVideos?.length > 0 && videoPopup()}
      {selectedVideo && videoDialog()}
    </>
  );
}
